import { type EventPropsOf, track, TrackingEventName } from '@heidi/analytics/events';
import { useLatestRef } from '@heidi/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Split-view panel analytics for the sessions page.
 *
 * The detail panel's `panel_opened` / `panel_closed` are emitted here from the
 * route transition (`hasDetail`), covering every close path — fullscreen-list
 * button, drag/keyboard collapse, back-nav — with one emitter. Mounting
 * already in detail (deep link) is not a transition and stays silent. The
 * list panel's events are emitted by the caller's explicit user actions via
 * the returned `trackPanel`.
 */
export const useSessionsPanelTracking = (hasDetail: boolean, sessionId: string) => {
  // Sticky id so panel-closed events can still attribute the session after
  // the route id has already cleared.
  const detailSessionIdRef = useRef(sessionId);
  if (sessionId) detailSessionIdRef.current = sessionId;

  const trackPanel = useCallback(
    (
      action: 'opened' | 'closed',
      panelType: EventPropsOf<TrackingEventName.PanelOpened>['panel_type'],
      panelSessionId: string
    ) => {
      track({
        event_name:
          action === 'opened'
            ? TrackingEventName.PanelOpened
            : TrackingEventName.PanelClosed,
        props: {
          panel_type: panelType,
          trigger: 'manual',
          ...(panelSessionId && { session_id: panelSessionId }),
        },
        options: { amplitude: false },
      });
    },
    []
  );

  const prevHasDetailRef = useRef(hasDetail);
  useEffect(() => {
    const prevHasDetail = prevHasDetailRef.current;
    prevHasDetailRef.current = hasDetail;
    if (prevHasDetail === hasDetail) return;
    trackPanel(
      hasDetail ? 'opened' : 'closed',
      'session_detail_panel',
      detailSessionIdRef.current
    );
  }, [hasDetail, trackPanel]);

  // List-panel events only make sense beside an open detail (split view) —
  // guarded here so call sites don't each need a hasDetail check.
  const hasDetailRef = useLatestRef(hasDetail);
  const listOpened = useCallback(() => {
    if (hasDetailRef.current) {
      trackPanel('opened', 'session_list_panel', detailSessionIdRef.current);
    }
  }, [trackPanel, hasDetailRef]);
  const listClosed = useCallback(() => {
    if (hasDetailRef.current) {
      trackPanel('closed', 'session_list_panel', detailSessionIdRef.current);
    }
  }, [trackPanel, hasDetailRef]);

  // Stable object so consumers can list one memo/callback dependency.
  return useMemo(() => ({ listOpened, listClosed }), [listOpened, listClosed]);
};
